import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import {
  LayoutDashboard,
  Store,
  Star,
  ShoppingBag,
  Tag,
  LogOut,
  Menu,
  X,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Shield,
  Home,
  Users,
  CreditCard,
  DollarSign,
  Flag,
  Package,
  Megaphone,
  Award
} from 'lucide-react';

const AdminLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [marketplaceOpen, setMarketplaceOpen] = useState(true);
  const [managementOpen, setManagementOpen] = useState(true);
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  const navigation = [
    { name: 'Dashboard', href: '/admin/dashboard', icon: LayoutDashboard },
  ];

  const managementNav = [
    { name: 'Announcements', href: '/admin/announcements', icon: Megaphone },
    { name: 'Promotions', href: '/admin/promotions', icon: Award },
  ];

  const marketplaceNav = [
    { name: 'Businesses', href: '/admin/marketplace/businesses', icon: Store },
    { name: 'Reviews', href: '/admin/marketplace/reviews', icon: Star },
    { name: 'Orders', href: '/admin/marketplace/orders', icon: ShoppingBag },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const goToMainDashboard = () => {
    navigate('/dashboard');
  };

  const isMarketplaceActive = marketplaceNav.some(item => 
    location.pathname.startsWith(item.href)
  );

  const isManagementActive = managementNav.some(item => 
    location.pathname.startsWith(item.href)
  );

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const NavItem = ({ item, onClick }) => {
    const isActive = location.pathname === item.href;
    
    return (
      <NavLink
        to={item.href}
        onClick={onClick}
        className={({ isActive }) => {
          const baseClasses = "group flex items-center text-xs font-medium rounded-lg transition-all border";
          const inactiveClasses = "text-gray-600 hover:bg-white hover:shadow-neomorph-hover border-transparent";
          const activeClasses = "bg-red-50 text-red-600 shadow-neomorph-inset border-red-100";
          
          return `${baseClasses} ${isActive ? activeClasses : inactiveClasses} ${
            sidebarCollapsed ? 'px-2 py-2 justify-center' : 'px-2.5 py-2'
          }`;
        }}
        title={sidebarCollapsed ? item.name : ''}
      >
        <div className={`p-1 rounded-md transition-all flex-shrink-0 ${
          isActive ? 'bg-red-100' : 'bg-red-50'
        }`}>
          <item.icon className={`h-3.5 w-3.5 ${isActive ? 'text-red-600' : 'text-red-500'}`} />
        </div>
        {!sidebarCollapsed && <span className="ml-2.5 flex-1">{item.name}</span>}
      </NavLink>
    );
  };

  const SectionHeader = ({ title, icon: Icon, isOpen, onClick }) => {
    if (sidebarCollapsed) {
      return (
        <button
          onClick={onClick}
          className="w-full flex items-center justify-center px-2 py-2 text-gray-400 hover:text-gray-600 transition-colors"
          title={title}
        >
          <Icon className="h-3.5 w-3.5" />
        </button>
      );
    }

    return (
      <button
        onClick={onClick}
        className={`w-full flex items-center justify-between px-2.5 py-2 text-xs font-medium rounded-lg transition-all border ${
          isOpen ? 'bg-red-50 text-red-600 shadow-neomorph-inset border-red-100' : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover border-transparent'
        }`}
      >
        <span className="flex items-center">
          <div className={`p-1 rounded-md mr-2 transition-all ${isOpen ? 'bg-red-100' : 'bg-red-50'}`}>
            <Icon className={`h-3.5 w-3.5 ${isOpen ? 'text-red-600' : 'text-red-500'}`} />
          </div>
          <span>{title}</span>
        </span>
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-[#E8ECF5]">
      {/* Mobile sidebar */}
      {sidebarOpen && (
        <div className="md:hidden fixed inset-0 z-40 flex">
          <div
            className="fixed inset-0 bg-gray-600/50 backdrop-blur-sm"
            onClick={() => setSidebarOpen(false)}
          />
          <div className="relative flex-1 flex flex-col max-w-xs w-full bg-[#E8ECF5] shadow-neomorph">
            <div className="absolute top-0 right-0 -mr-12 pt-2">
              <button
                className="ml-1 flex items-center justify-center h-10 w-10 rounded-lg bg-white shadow-neomorph hover:shadow-neomorph-hover transition-all text-gray-500"
                onClick={() => setSidebarOpen(false)}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
              <div className="flex-shrink-0 flex items-center px-4 mb-6">
                <div className="flex items-center space-x-2">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-red-500 to-red-600 shadow-md shadow-red-500/30 flex items-center justify-center">
                    <Shield className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-xl font-bold text-gray-800">Admin</span>
                </div>
              </div>
              <nav className="mt-5 px-3 space-y-1">
                {navigation.map((item) => (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) => {
                      const baseClasses = "group flex items-center px-3 py-2.5 text-xs font-medium rounded-lg transition-all border";
                      const inactiveClasses = "text-gray-600 hover:bg-white hover:shadow-neomorph-hover border-transparent";
                      const activeClasses = "bg-red-50 text-red-600 shadow-neomorph-inset border-red-100";
                      return `${baseClasses} ${isActive ? activeClasses : inactiveClasses}`;
                    }}
                  >
                    {({ isActive }) => (
                      <>
                        <div className={`p-1.5 rounded-md mr-3 transition-all ${isActive ? 'bg-red-100' : 'bg-red-50'}`}>
                          <item.icon className={`h-3.5 w-3.5 ${isActive ? 'text-red-600' : 'text-red-500'}`} />
                        </div>
                        <span className="flex-1">{item.name}</span>
                      </>
                    )}
                  </NavLink>
                ))}

                {/* Management Section */}
                <div className="pt-4 mt-4 border-t border-primary-100/50">
                  <button
                    onClick={() => setManagementOpen(!managementOpen)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 text-xs font-medium rounded-lg transition-all border ${
                      isManagementActive ? 'bg-red-50 text-red-600 shadow-neomorph-inset border-red-100' : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover border-transparent'
                    }`}
                  >
                    <span className="flex items-center">
                      <div className={`p-1.5 rounded-md mr-3 transition-all ${isManagementActive ? 'bg-red-100' : 'bg-red-50'}`}>
                        <Megaphone className={`h-3.5 w-3.5 ${isManagementActive ? 'text-red-600' : 'text-red-500'}`} />
                      </div>
                      <span>Management</span>
                    </span>
                    <ChevronDown className={`h-3.5 w-3.5 transition-transform ${managementOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {managementOpen && (
                    <div className="mt-1 space-y-1 pl-11">
                      {managementNav.map((item) => (
                        <NavLink
                          key={item.name}
                          to={item.href}
                          onClick={() => setSidebarOpen(false)}
                          className={({ isActive }) => {
                            const baseClasses = "group flex items-center px-3 py-2 text-xs font-medium rounded-lg transition-all border";
                            const inactiveClasses = "text-gray-600 hover:bg-white hover:shadow-neomorph-hover border-transparent";
                            const activeClasses = "bg-red-50 text-red-600 shadow-neomorph-inset border-red-100";
                            return `${baseClasses} ${isActive ? activeClasses : inactiveClasses}`;
                          }}
                        >
                          {({ isActive }) => (
                            <>
                              <div className={`p-1 rounded-md mr-3 transition-all ${isActive ? 'bg-red-100' : 'bg-red-50'}`}>
                                <item.icon className={`h-3 w-3 ${isActive ? 'text-red-600' : 'text-red-500'}`} />
                              </div>
                              <span>{item.name}</span>
                            </>
                          )}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>

                {/* Marketplace Section */}
                <div className="pt-4 mt-4 border-t border-primary-100/50">
                  <button
                    onClick={() => setMarketplaceOpen(!marketplaceOpen)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 text-xs font-medium rounded-lg transition-all border ${
                      isMarketplaceActive ? 'bg-red-50 text-red-600 shadow-neomorph-inset border-red-100' : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover border-transparent'
                    }`}
                  >
                    <span className="flex items-center">
                      <div className={`p-1.5 rounded-md mr-3 transition-all ${isMarketplaceActive ? 'bg-red-100' : 'bg-red-50'}`}>
                        <Store className={`h-3.5 w-3.5 ${isMarketplaceActive ? 'text-red-600' : 'text-red-500'}`} />
                      </div>
                      <span>Marketplace</span>
                    </span>
                    <ChevronDown className={`h-3.5 w-3.5 transition-transform ${marketplaceOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {marketplaceOpen && (
                    <div className="mt-1 space-y-1 pl-11">
                      {marketplaceNav.map((item) => (
                        <NavLink
                          key={item.name}
                          to={item.href}
                          onClick={() => setSidebarOpen(false)}
                          className={({ isActive }) => {
                            const baseClasses = "group flex items-center px-3 py-2 text-xs font-medium rounded-lg transition-all border";
                            const inactiveClasses = "text-gray-600 hover:bg-white hover:shadow-neomorph-hover border-transparent";
                            const activeClasses = "bg-red-50 text-red-600 shadow-neomorph-inset border-red-100";
                            return `${baseClasses} ${isActive ? activeClasses : inactiveClasses}`;
                          }}
                        >
                          {({ isActive }) => (
                            <>
                              <div className={`p-1 rounded-md mr-3 transition-all ${isActive ? 'bg-red-100' : 'bg-red-50'}`}>
                                <item.icon className={`h-3 w-3 ${isActive ? 'text-red-600' : 'text-red-500'}`} />
                              </div>
                              <span>{item.name}</span>
                            </>
                          )}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              </nav>
            </div>
            <div className="flex-shrink-0 border-t border-primary-100/50 p-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-red-500 to-red-600 shadow-md shadow-red-500/30 flex items-center justify-center">
                  <span className="text-white font-medium">
                    {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className={`hidden md:flex md:flex-col md:fixed md:inset-y-0 transition-all duration-300 ${
        sidebarCollapsed ? 'md:w-16' : 'md:w-64'
      }`}>
        <div className={`flex-1 flex flex-col min-h-0 bg-[#E8ECF5] shadow-neomorph m-3 rounded-xl border border-primary-100/30 overflow-hidden transition-all duration-300`}>
          <div className="flex-1 flex flex-col pt-3 pb-3 overflow-y-auto">
            {/* Logo */}
            <div className={`flex items-center flex-shrink-0 ${sidebarCollapsed ? 'justify-center px-2' : 'px-4'} mb-3`}>
              <div className="flex items-center space-x-2.5">
                <div className={`${sidebarCollapsed ? 'w-8 h-8' : 'w-10 h-10'} rounded-xl bg-gradient-to-br from-red-500 to-red-600 shadow-md shadow-red-500/30 flex items-center justify-center flex-shrink-0`}>
                  <Shield className={`${sidebarCollapsed ? 'h-4 w-4' : 'h-5 w-5'} text-white`} />
                </div>
                {!sidebarCollapsed && (
                  <div>
                    <span className="text-lg font-bold text-gray-800">Admin</span>
                    <p className="text-[10px] text-gray-500 mt-0.5">Control Panel</p>
                  </div>
                )}
              </div>
            </div>

            {/* Toggle Button */}
            <div className={`${sidebarCollapsed ? 'px-2' : 'px-3'} mb-3`}>
              <button
                onClick={toggleSidebar}
                className={`w-full flex items-center justify-center p-1.5 bg-white rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all text-gray-500 hover:text-gray-700`}
                title={sidebarCollapsed ? 'Expand' : 'Collapse'}
              >
                {sidebarCollapsed ? (
                  <ChevronRight className="h-3.5 w-3.5" />
                ) : (
                  <ChevronLeft className="h-3.5 w-3.5" />
                )}
              </button>
            </div>
            
            {/* Admin Badge */}
            {!sidebarCollapsed && (
              <div className="mx-3 mb-3 p-2.5 bg-white rounded-xl shadow-neomorph-inset border border-red-100/30">
                <div className="flex items-center space-x-2">
                  <div className="p-1 bg-red-50 rounded-lg">
                    <Shield className="h-3.5 w-3.5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500">Logged in as</p>
                    <p className="text-xs font-semibold text-gray-800">Administrator</p>
                  </div>
                </div>
              </div>
            )}

            {/* Go to Main Dashboard Button */}
            <div className={`${sidebarCollapsed ? 'px-2' : 'px-3'} mb-3`}>
              <button
                onClick={goToMainDashboard}
                className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center px-2 py-2' : 'px-2.5 py-2'} text-xs font-medium rounded-lg transition-all border text-gray-600 hover:bg-white hover:shadow-neomorph-hover border-transparent bg-white shadow-neomorph hover:shadow-neomorph-hover`}
                title={sidebarCollapsed ? 'Main Dashboard' : ''}
              >
                <Home className={`h-3.5 w-3.5 ${sidebarCollapsed ? '' : 'mr-2.5'} text-blue-500`} />
                {!sidebarCollapsed && <span>Main Dashboard</span>}
              </button>
            </div>

            <nav className={`flex-1 ${sidebarCollapsed ? 'px-2' : 'px-3'} space-y-1`}>
              {navigation.map((item) => (
                <NavItem key={item.name} item={item} />
              ))}

              {/* Management Section */}
              <div className="pt-2.5 mt-2.5 border-t border-primary-100/50">
                <SectionHeader
                  title="Management"
                  icon={Megaphone}
                  isOpen={managementOpen}
                  onClick={() => setManagementOpen(!managementOpen)}
                />
                
                {managementOpen && !sidebarCollapsed && (
                  <div className="mt-1 space-y-1">
                    {managementNav.map((item) => (
                      <NavItem key={item.name} item={item} />
                    ))}
                  </div>
                )}
                {managementOpen && sidebarCollapsed && (
                  <div className="mt-1 space-y-1">
                    {managementNav.map((item) => (
                      <NavItem key={item.name} item={item} />
                    ))}
                  </div>
                )}
              </div>

              {/* Marketplace Section */}
              <div className="pt-2.5 mt-2.5 border-t border-primary-100/50">
                <SectionHeader
                  title="Marketplace"
                  icon={Store}
                  isOpen={marketplaceOpen}
                  onClick={() => setMarketplaceOpen(!marketplaceOpen)}
                />
                
                {marketplaceOpen && !sidebarCollapsed && (
                  <div className="mt-1 space-y-1">
                    {marketplaceNav.map((item) => (
                      <NavItem key={item.name} item={item} />
                    ))}
                  </div>
                )}
                {marketplaceOpen && sidebarCollapsed && (
                  <div className="mt-1 space-y-1">
                    {marketplaceNav.map((item) => (
                      <NavItem key={item.name} item={item} />
                    ))}
                  </div>
                )}
              </div>
            </nav>
          </div>
          
          {/* User Profile Section */}
          <div className={`flex-shrink-0 border-t border-primary-100/50 ${sidebarCollapsed ? 'p-2.5' : 'p-3.5'}`}>
            <div className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'space-x-2.5'}`}>
              <div className={`${sidebarCollapsed ? 'w-8 h-8' : 'w-10 h-10'} rounded-xl bg-gradient-to-br from-red-500 to-red-600 shadow-md shadow-red-500/30 flex items-center justify-center flex-shrink-0`}>
                <span className={`text-white font-medium ${sidebarCollapsed ? 'text-[10px]' : 'text-sm'}`}>
                  {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
                </span>
              </div>
              {!sidebarCollapsed && (
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-gray-800 truncate">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-[10px] text-gray-500 truncate">{user?.email}</p>
                </div>
              )}
              {!sidebarCollapsed && (
                <button
                  onClick={handleLogout}
                  className="p-1.5 bg-white rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all text-gray-500 hover:text-red-600 flex-shrink-0"
                  title="Logout"
                >
                  <LogOut className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            {sidebarCollapsed && (
              <button
                onClick={handleLogout}
                className="w-full mt-2 p-1.5 bg-white rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all text-gray-500 hover:text-red-600 flex items-center justify-center"
                title="Logout"
              >
                <LogOut className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className={`transition-all duration-300 ${sidebarCollapsed ? 'md:pl-20' : 'md:pl-68'} flex flex-col flex-1`}>
        {/* Top header */}
        <div className="sticky top-0 z-10 flex-shrink-0 bg-[#E8ECF5] p-3">
          <div className="bg-white rounded-xl shadow-neomorph border border-primary-100/30">
            <div className="flex h-12 items-center justify-between px-4">
              <div className="flex items-center">
                <button
                  type="button"
                  className="p-1.5 rounded-lg bg-white shadow-neomorph hover:shadow-neomorph-hover text-gray-500 transition-all md:hidden mr-3"
                  onClick={() => setSidebarOpen(true)}
                >
                  <Menu className="h-4 w-4" />
                </button>
                <div className="flex items-center">
                  <div className="w-1 h-4 bg-gradient-to-b from-red-400 to-red-600 rounded-full mr-2" />
                  <h1 className="text-sm font-semibold text-gray-800">Admin Dashboard</h1>
                  <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-lg text-[10px] font-medium bg-red-50 text-red-600 border border-red-100 hidden sm:inline-flex">
                    <Shield className="h-2.5 w-2.5 mr-0.5" />
                    Admin
                  </span>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={goToMainDashboard}
                  className="flex items-center px-2.5 py-1 text-[10px] font-medium rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-all border border-blue-100"
                >
                  <Home className="h-3.5 w-3.5 mr-1" />
                  <span className="hidden sm:inline">Main App</span>
                </button>
                <button
                  onClick={handleLogout}
                  className="p-1.5 rounded-lg bg-white shadow-neomorph hover:shadow-neomorph-hover transition-all text-gray-500 hover:text-red-600"
                  title="Logout"
                >
                  <LogOut className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 p-3">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>

      <style jsx>{`
        .shadow-neomorph {
          box-shadow: 6px 6px 11px #d0d6e0, -6px -6px 11px #ffffff;
        }
        .shadow-neomorph-hover {
          box-shadow: 4px 4px 7px #d0d6e0, -4px -4px 7px #ffffff;
        }
        .shadow-neomorph-inset {
          box-shadow: inset 2.5px 2.5px 5px #d0d6e0, inset -2.5px -2.5px 5px #ffffff;
        }
      `}</style>
    </div>
  );
};

export default AdminLayout;